import { ScopeAccount } from '../../../../types/responses/Interoperability';

/**
 * Seleção de contas do convite (MV2-1714).
 *
 * Espelha o TenantBindScopeResolver do backend. A duplicação é deliberada: a
 * tela precisa mostrar o total antes de enviar, e o backend não pode confiar
 * no que a tela mandou. As duas pontas aplicam a mesma regra por motivos
 * diferentes.
 */

export type ScopeMode = 'ALL' | 'ALL_EXCEPT' | 'SELECTED';

/**
 * Quem está fora por exclusão: o que foi marcado, e a subárvore abaixo (MV2-1847).
 *
 * "Todas, exceto" é uma exclusão, e exclusão cascateia — excluir a filial exclui a
 * loja abaixo dela. Espelha o que o backend faz ao resolver o escopo do convite.
 *
 * Só o ALL_EXCEPT: o SELECTED é "exatamente estas", e escolher a filial sem a loja
 * continua sendo uma escolha legítima.
 */
const excludedWithSubAccounts = (
  accounts: ScopeAccount[],
  marked: Set<string>,
): Set<string> => {
  const parentOf = new Map(accounts.map((account) => [account.tenant_id, account.tenant_parent_id]));
  const excluded = new Set<string>();

  accounts.forEach((account) => {
    let current: string | null | undefined = account.tenant_id;

    // Sobe até a raiz procurando quem foi marcado. O teto de passos é a defesa contra
    // ciclo em tenant_parent_id: sem ele, um pai apontando para a própria descendente
    // pendura a aba inteira num laço infinito.
    for (let step = 0; current && step <= accounts.length; step += 1) {
      if (marked.has(current)) {
        excluded.add(account.tenant_id);
        return;
      }
      current = parentOf.get(current);
    }
  });

  return excluded;
};

/**
 * Contas efetivamente no contrato.
 *
 * A matriz entra sempre, em qualquer modo — inclusive se alguém a marcar como
 * exceção. Contrato sem conta nenhuma não compartilha nada.
 */
export const resolveScope = (
  accounts: ScopeAccount[],
  mode: ScopeMode,
  marked: Set<string>,
): Set<string> => {
  const root = accounts.find((account) => account.root);
  const scope = new Set<string>();
  const excluded = mode === 'ALL_EXCEPT'
    ? excludedWithSubAccounts(accounts, marked)
    : marked;

  accounts.forEach((account) => {
    const isMarked = marked.has(account.tenant_id);

    if (mode === 'ALL') {
      scope.add(account.tenant_id);
      return;
    }
    if (mode === 'ALL_EXCEPT' && !excluded.has(account.tenant_id)) {
      scope.add(account.tenant_id);
      return;
    }
    if (mode === 'SELECTED' && isMarked) {
      scope.add(account.tenant_id);
    }
  });

  if (root) {
    scope.add(root.tenant_id);
  }
  return scope;
};

/**
 * Quantas contas a exclusão tirou de fato, para o chip vermelho.
 *
 * Sai do escopo resolvido, e não do tamanho da marcação: com a cascata, marcar uma
 * filial pode tirar três contas, e o chip diria "1 fora do contrato" ao lado de um
 * total que já desceu três.
 */
export const excludedCount = (accounts: ScopeAccount[], scope: Set<string>): number =>
  accounts.filter((account) => !scope.has(account.tenant_id)).length;

/** Totais do rodapé do bloco: sempre coerentes com a seleção efetiva. */
export const scopeTotals = (
  accounts: ScopeAccount[],
  scope: Set<string>,
): { accountCount: number; stationCount: number } => {
  const inScope = accounts.filter((account) => scope.has(account.tenant_id));

  return {
    accountCount: inScope.length,
    stationCount: inScope.reduce((total, account) => total + (account.station_count || 0), 0),
  };
};

/** Busca por nome ou UF, como no protótipo. */
export const filterAccounts = (accounts: ScopeAccount[], search: string): ScopeAccount[] => {
  const term = search.trim().toLowerCase();

  if (!term) {
    return accounts;
  }
  return accounts.filter((account) => account.name?.toLowerCase().includes(term));
};

/**
 * No modo "todas, exceto", marcar significa EXCLUIR — e a marcação é vermelha
 * com ×, não azul com ✓. Confundir os dois inverteria o contrato inteiro.
 */
export const isExclusionMode = (mode: ScopeMode): boolean => mode === 'ALL_EXCEPT';

/** Ação em massa: o rótulo muda com o modo, e não existe no modo "todas". */
export const bulkLabelFor = (mode: ScopeMode): string | null => {
  if (mode === 'SELECTED') return 'select all';
  if (mode === 'ALL_EXCEPT') return 'clear exclusions';
  return null;
};
